import NiceModal, { useModal } from "@ebay/nice-modal-react"
import { ShieldAlert } from "lucide-react"
import { useCallback, useEffect, useRef } from "react"
import type { PermissionPromptResponse, PermissionRequestNotification } from "../../nest/client"
import { Modal } from "../ui"

interface PermissionPromptModalProps {
    request: PermissionRequestNotification
    signal?: AbortSignal
    registerDecision?: (settle: (response: PermissionPromptResponse) => void) => () => void
}

function permissionDetail(request: PermissionRequestNotification): string {
    return request.command || request.blockedPath || request.description || request.reason || request.toolName || "The provider requested an operation."
}

function permissionTitle(request: PermissionRequestNotification): string {
    if (request.title?.trim()) return request.title
    if (request.mode === "auto") return "Review blocked operation"
    switch (request.kind) {
        case "command":
            return "Allow command?"
        case "file-change":
            return "Allow file change?"
        case "tool":
            return "Allow tool?"
        case "permissions":
            return "Allow permissions?"
    }
}

function permissionInputDetail(input: PermissionRequestNotification["input"]): string | null {
    if (input === null) return null
    const encoded = JSON.stringify(input, null, 2)
    return encoded || null
}

export function PermissionPromptDialog({ request, onDecision }: PermissionPromptModalProps & { onDecision: (response: PermissionPromptResponse) => void }) {
    const detail = permissionDetail(request)
    const inputDetail = permissionInputDetail(request.input)
    const review = request.review
    return (
        <Modal title={permissionTitle(request)} onClose={() => onDecision({ decision: "deny", message: "Denied by user" })} hideSeparator>
            <div className="flex flex-col gap-4 text-sm text-base-content">
                <div className="flex items-start gap-3 border border-warning/30 bg-warning/10 p-3">
                    <ShieldAlert size={18} className="mt-0.5 shrink-0 text-warning" />
                    <div className="min-w-0">
                        <p className="font-medium">{request.providerId} wants permission to continue</p>
                        <p className="mt-1 text-xs text-muted">
                            {request.mode === "auto" ? "Automatic review escalated this operation." : "This operation requires your approval."}
                        </p>
                    </div>
                </div>

                <pre className="max-h-64 overflow-auto whitespace-pre-wrap break-words border border-border bg-base-200 p-3 font-mono text-xs">{detail}</pre>

                {inputDetail && (
                    <details className="border border-border bg-base-200/40 p-3 text-xs">
                        <summary className="cursor-pointer font-medium text-base-content">Provider request details</summary>
                        <pre className="mt-3 max-h-48 overflow-auto whitespace-pre-wrap break-words font-mono text-xs">{inputDetail}</pre>
                    </details>
                )}

                {(request.cwd || review?.rationale || review?.riskLevel) && (
                    <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1 text-xs">
                        {request.cwd && (
                            <>
                                <dt className="text-muted">Directory</dt>
                                <dd className="break-all font-mono">{request.cwd}</dd>
                            </>
                        )}
                        {review?.riskLevel && (
                            <>
                                <dt className="text-muted">Risk</dt>
                                <dd className="capitalize">{review.riskLevel}</dd>
                            </>
                        )}
                        {review?.rationale && (
                            <>
                                <dt className="text-muted">Review</dt>
                                <dd>{review.rationale}</dd>
                            </>
                        )}
                    </dl>
                )}

                <div className="flex flex-col gap-2 sm:flex-row">
                    <button
                        type="button"
                        autoFocus
                        className="btn min-h-10 flex-1 border border-border bg-base-200 px-4 py-2.5 font-medium text-base-content hover:bg-base-300"
                        onClick={() => onDecision({ decision: "deny", message: "Denied by user" })}
                    >
                        Deny
                    </button>
                    <button
                        type="button"
                        className="btn min-h-10 flex-1 bg-warning px-4 py-2.5 font-medium text-warning-content hover:bg-warning/90"
                        onClick={() => onDecision({ decision: "allow" })}
                    >
                        {request.mode === "auto" ? "Override and allow" : "Allow once"}
                    </button>
                </div>
            </div>
        </Modal>
    )
}

const cancelledPermission: PermissionPromptResponse = { decision: "deny", message: "Permission request cancelled" }

export const PermissionPromptModal = NiceModal.create(({ request, registerDecision, signal }: PermissionPromptModalProps) => {
    const modal = useModal()
    const settled = useRef(false)
    const settle = useCallback(
        (response: PermissionPromptResponse) => {
            if (settled.current) return
            settled.current = true
            modal.resolve(response)
            modal.remove()
        },
        [modal]
    )
    useEffect(() => registerDecision?.(settle), [registerDecision, settle])
    useEffect(() => {
        const abort = () => settle(cancelledPermission)
        signal?.addEventListener("abort", abort, { once: true })
        if (signal?.aborted) abort()
        return () => signal?.removeEventListener("abort", abort)
    }, [signal, settle])
    return <PermissionPromptDialog request={request} onDecision={settle} />
})

let promptQueue: Promise<void> = Promise.resolve()
// A node broadcasts the same request to the UI and pinned controller sockets.
// Share pending decisions and retain only 128 settled keys for delayed delivery;
// no request contents are persisted or reused for a new permission request.
const prompts = new Map<string, Promise<PermissionPromptResponse>>()
const settledKeys: string[] = []

export function showPermissionPrompt(
    request: PermissionRequestNotification,
    { nodeId, registerDecision, signal }: { nodeId: string; registerDecision?: PermissionPromptModalProps["registerDecision"]; signal?: AbortSignal }
): Promise<PermissionPromptResponse> {
    const key = JSON.stringify([nodeId, request.sessionId, request.requestId])
    const existing = prompts.get(key)
    if (existing) return existing
    let detach = () => {}
    const cancelled = new Promise<PermissionPromptResponse>((resolve) => {
        const abort = () => resolve(cancelledPermission)
        signal?.addEventListener("abort", abort, { once: true })
        detach = () => signal?.removeEventListener("abort", abort)
        if (signal?.aborted) abort()
    })
    const prompt = promptQueue.then(async () => {
        if (signal?.aborted) return cancelledPermission
        const response = await NiceModal.show(PermissionPromptModal, { request, registerDecision, signal })
        if (typeof response !== "object" || response === null || !("decision" in response)) {
            return { decision: "deny", message: "Permission prompt closed without a decision" } satisfies PermissionPromptResponse
        }
        const decision = response.decision
        if (decision !== "allow" && decision !== "deny") {
            return { decision: "deny", message: "Permission prompt returned an invalid decision" } satisfies PermissionPromptResponse
        }
        const message = "message" in response && typeof response.message === "string" ? response.message : undefined
        return { decision, ...(message ? { message } : {}) } satisfies PermissionPromptResponse
    })
    const shared = Promise.race([prompt, cancelled])
        .finally(detach)
        .then(
            (response) => {
                settledKeys.push(key)
                if (settledKeys.length > 128) {
                    const oldest = settledKeys.shift()
                    if (oldest !== undefined) prompts.delete(oldest)
                }
                return response
            },
            (error: unknown) => {
                prompts.delete(key)
                throw error
            }
        )
    prompts.set(key, shared)
    // Cancellation settles the caller immediately, but a queued cancellation
    // must still wait its turn before later ordinary dialogs can mount.
    promptQueue = prompt.then(
        () => undefined,
        () => undefined
    )
    return shared
}
